import type { InlineReferenceEditorHandle } from '@crowdex/ui/components/inline-reference-editor';
import { type RefObject, useEffect } from 'react';
import { useStore } from 'zustand';
import type { DesktopShellStore } from './store';

/**
 * Side effects tied to the active conversation: mirror the thread id into the
 * URL, persist the current composer text as this pane's draft, and focus the
 * composer after a programmatic conversation switch or when a draft is given to it. Extracted from three
 * inline effects in `DesktopShell`.
 */
export function useConversationSwitchEffects({
  store,
  preview,
  chatId,
  focusAfterSwitchRef,
  composerRef,
}: {
  store: DesktopShellStore;
  preview: boolean;
  chatId: string;
  focusAfterSwitchRef: RefObject<boolean>;
  composerRef: RefObject<InlineReferenceEditorHandle | null>;
}): void {
  useEffect(() => {
    if (preview) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('threadId') === chatId && !url.searchParams.has('newSession')) return;
    url.searchParams.set('threadId', chatId);
    // `newSession` asked for the conversation this shell opened: once it is there, the address
    // names it, so coming back to this address (Back, leaving Settings, a reload) shows it again
    // instead of remounting the shell on yet another new conversation.
    url.searchParams.delete('newSession');
    window.history.replaceState(window.history.state, '', url);
  }, [chatId, preview]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(chatId): the composer is focused once the conversation switched to shows.
  useEffect(() => {
    if (!focusAfterSwitchRef.current) return;
    focusAfterSwitchRef.current = false;
    window.requestAnimationFrame(() => composerRef.current?.focus());
  }, [chatId, focusAfterSwitchRef, composerRef]);

  useComposerFocusRequest(store, composerRef);
}

/**
 * Puts the caret at the end of the active composer when something else gave it its draft
 * (search's "Start a conversation: “…”"), once the dialog that asked has closed.
 */
export function useComposerFocusRequest(
  store: DesktopShellStore,
  composerRef: RefObject<InlineReferenceEditorHandle | null>,
): void {
  const request = useStore(store, (state) => state.composerFocusRequest);
  useEffect(() => {
    if (request === 0) return;
    const frame = window.requestAnimationFrame(() => {
      const composer = composerRef.current;
      if (composer === null) return;
      const { chatId, paneDrafts } = store.getState();
      const length = paneDrafts[chatId]?.length ?? 0;
      composer.focus();
      composer.setSelectionRange(length, length);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [composerRef, request, store]);
}
